import {
    api,
    apiErrorMessage,
    oneSpec,
    useAuthStore,
    useIsPlatformHost,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Checkbox,
    ConfirmDialog,
    DateTime,
    DetailRow,
    Field,
    FieldGrid,
    Input,
    PageHeader,
    PublicId,
    ReasonDialog,
    SearchableSelect,
    StatusBadge,
    statusLabel,
    UnsavedGuard,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import type { Tenant, TenantIntegration } from '@/api/types';
import { ActivityCard } from '@/components/activity';
import { DetailBody } from '@/components/detail';
import { detailTitle } from '@/components/detail-title';
import { optionsOf } from '@/lib/options';
import { P } from '@/lib/permissions';
import { useOne, useWrite } from '@/lib/queries';
import { brandName, legalName } from '@/lib/tenants';
import { AiSection } from '@/pages/tenants/ai-section';
import { LedgerSection } from '@/pages/tenants/ledger-section';
import { LogoSection } from '@/pages/tenants/logo-section';
import { OnboardingSection } from '@/pages/tenants/onboarding-section';
import { TaxCodesSection } from '@/pages/tenants/tax-codes-section';
import { TimezoneSection } from '@/pages/tenants/timezone-section';

const DOMAIN_KINDS = ['pay', 'app', 'admin', 'api', 'ledger', 'docs'] as const;
// BM-70 § 8: NMI is a provider account of the tenant pool now, so the integration form no longer offers it, and the
// nmi activation step completes itself when the tenant has a verified provider account
const PROVIDERS = ['anrok', 'corpay', 'docusign'] as const;
const AUTOMATIC_STEPS: readonly string[] = ['nmi'];
const STEPS = [
    'legal',
    'brand',
    'domains',
    'mail_domain',
    'policies',
    'nmi',
    'anrok',
    'corpay',
    'admin',
] as const;

/** The reads of `/tenants/:id` (BM-46 §2.0). */
export const tenantReads = (id: string | undefined) => ({
    tenant: oneSpec(`/staff/tenants/${id ?? ''}`),
});

export function TenantPage() {
    const { id = '' } = useParams();
    const query = useOne<Tenant>(tenantReads(id).tenant.path);
    const can = useAuthStore((s) => s.can);
    // BM-145: provisioning is a platform concern; a tenant's own board never shows its Ledger card
    const platformHost = useIsPlatformHost();
    const [error, setError] = useState<string | null>(null);
    const [domain, setDomain] = useState({ kind: 'pay', hostname: '' });
    const [integration, setIntegration] = useState({
        provider: 'anrok',
        config: '{}',
        secret_name: '',
        verified: false,
    });
    const [confirm, setConfirm] = useState<'activate' | 'suspend' | null>(null);
    const onError = (e: unknown) => setError(apiErrorMessage(e));
    const inv = ['/staff/tenants'];
    const addDomain = useWrite(
        async (body: { kind: string; hostname: string }) =>
            (await api().post<Tenant>(`/staff/tenants/${id}/domains`, body))
                .data,
        inv,
    );
    const markDomain = useWrite(
        async (body: {
            domain: string;
            verified: boolean;
            certificate_ready: boolean;
            apple_pay_verified?: boolean;
        }) =>
            (
                await api().patch<Tenant>(
                    `/staff/tenants/${id}/domains/${body.domain}`,
                    {
                        verified: body.verified,
                        certificate_ready: body.certificate_ready,
                        // BM-70 § 11: sent only by the Apple Pay box; absent leaves the stored flag as it is
                        apple_pay_verified: body.apple_pay_verified,
                    },
                )
            ).data,
        inv,
    );
    const setIntegrationCall = useWrite(
        async (body: {
            provider: string;
            config: Record<string, unknown>;
            secret_name: string;
            verified: boolean;
        }) =>
            (
                await api().put<Tenant>(
                    `/staff/tenants/${id}/integrations/${body.provider}`,
                    {
                        config: body.config,
                        secret_name: body.secret_name,
                        verified: body.verified,
                    },
                )
            ).data,
        inv,
    );
    const setStep = useWrite(
        async (body: { step: string; state: string }) =>
            (
                await api().put<Tenant>(
                    `/staff/tenants/${id}/activation/${body.step}`,
                    { state: body.state },
                )
            ).data,
        inv,
    );
    const activate = useWrite(
        async () =>
            (await api().post<Tenant>(`/staff/tenants/${id}/activate`)).data,
        inv,
    );
    const suspend = useWrite(
        async (reason: string) =>
            (
                await api().post<Tenant>(`/staff/tenants/${id}/suspend`, {
                    reason,
                })
            ).data,
        inv,
    );
    return (
        <div>
            {/* BM-46 §2.9: leaving with unsaved changes asks first */}
            <UnsavedGuard
                dirty={
                    domain.hostname.trim() !== '' ||
                    integration.config !== '{}' ||
                    integration.secret_name !== ''
                }
            />
            <PageHeader
                crumbs={[{ label: 'Tenants', href: '/tenants' }]}
                {...detailTitle(
                    query,
                    query.data ? brandName(query.data.data) : undefined,
                    id,
                )}
                meta={
                    <>
                        <PublicId id={id} short={false} />
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </>
                }
            />
            <DetailBody query={query} title="Tenant">
                {(t) => {
                    const integrations =
                        t.integrations as unknown as TenantIntegration[];
                    return (
                        <div className="space-y-4">
                            {error ? (
                                <p
                                    role="alert"
                                    className="text-destructive text-sm"
                                >
                                    {error}
                                </p>
                            ) : null}
                            <Card>
                                <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                                    <CardTitle className="text-base">
                                        {legalName(t)}{' '}
                                        <StatusBadge
                                            status={t.status}
                                            domain="tenant"
                                        />
                                    </CardTitle>
                                    <div className="flex gap-2">
                                        {t.status !== 'active' ? (
                                            <Button
                                                size="sm"
                                                onClick={() =>
                                                    setConfirm('activate')
                                                }
                                            >
                                                Activate
                                            </Button>
                                        ) : (
                                            <Button
                                                size="sm"
                                                variant="destructive"
                                                onClick={() =>
                                                    setConfirm('suspend')
                                                }
                                            >
                                                Suspend
                                            </Button>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <FieldGrid>
                                        <DetailRow
                                            label="Id"
                                            value={t.id}
                                            layout="stacked"
                                        />
                                        <DetailRow
                                            label="Slug"
                                            value={t.slug}
                                            layout="stacked"
                                        />
                                        <DetailRow
                                            label="Descriptor prefix"
                                            value={t.descriptor_prefix}
                                            layout="stacked"
                                        />
                                        <DetailRow
                                            label="Mail domain"
                                            layout="stacked"
                                        >
                                            {t.mail_domain ?? '—'}
                                            {t.mail_domain_verified_at ? (
                                                <span className="text-muted-foreground ml-1 text-xs">
                                                    verified
                                                </span>
                                            ) : null}
                                        </DetailRow>
                                        <DetailRow
                                            label="Created"
                                            layout="stacked"
                                        >
                                            <DateTime
                                                zone="inline"
                                                value={t.created_at}
                                                empty="—"
                                            />
                                        </DetailRow>
                                    </FieldGrid>
                                </CardContent>
                            </Card>
                            {can(P.tenantsManage) ? (
                                <LogoSection tenant={t} />
                            ) : null}
                            {can(P.tenantsManage) ? (
                                <OnboardingSection tenant={t} />
                            ) : null}
                            <TimezoneSection
                                tenant={t}
                                editable={can(P.tenantsManage)}
                            />
                            {platformHost && can(P.tenantsManage) ? (
                                <LedgerSection tenant={t} />
                            ) : null}
                            {can(P.taxRead) ? <TaxCodesSection /> : null}
                            <AiSection tenant={t} />
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-sm font-medium">
                                        Activation checklist
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ul
                                        className="divide-y text-sm"
                                        data-testid="activation-steps"
                                    >
                                        {STEPS.map((step) => {
                                            const s = t.activation.find(
                                                (a) => a.step === step,
                                            );
                                            return (
                                                <li
                                                    key={step}
                                                    className="flex flex-wrap items-center justify-between gap-2 py-2"
                                                >
                                                    <span>
                                                        <StatusBadge
                                                            status={
                                                                s?.state ??
                                                                'pending'
                                                            }
                                                        />{' '}
                                                        <span className="ml-2">
                                                            {step.replace(
                                                                /_/g,
                                                                ' ',
                                                            )}
                                                        </span>
                                                        {s?.detail ? (
                                                            <span className="text-muted-foreground ml-2 text-xs">
                                                                {s.detail}
                                                            </span>
                                                        ) : null}
                                                    </span>
                                                    <span className="flex items-center gap-2 text-xs">
                                                        {s?.done_at ? (
                                                            <DateTime
                                                                value={
                                                                    s.done_at
                                                                }
                                                                empty="—"
                                                            />
                                                        ) : null}
                                                        {AUTOMATIC_STEPS.includes(
                                                            step,
                                                        ) ? (
                                                            <span className="text-muted-foreground">
                                                                Done when a
                                                                verified
                                                                provider account
                                                                exists
                                                            </span>
                                                        ) : (
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() =>
                                                                    setStep.mutate(
                                                                        {
                                                                            step,
                                                                            state:
                                                                                s?.state ===
                                                                                'done'
                                                                                    ? 'pending'
                                                                                    : 'done',
                                                                        },
                                                                        {
                                                                            onError,
                                                                        },
                                                                    )
                                                                }
                                                            >
                                                                {s?.state ===
                                                                'done'
                                                                    ? 'Undo'
                                                                    : 'Mark done'}
                                                            </Button>
                                                        )}
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-sm font-medium">
                                        Domains
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <ul
                                        className="divide-y text-sm"
                                        data-testid="tenant-domains"
                                    >
                                        {t.domains.map((d) => (
                                            <li
                                                key={d.id}
                                                className="space-y-1 py-2"
                                            >
                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                    <span>
                                                        <span className="font-mono">
                                                            {d.hostname}
                                                        </span>
                                                        <span className="text-muted-foreground ml-2 text-xs">
                                                            {statusLabel(
                                                                d.kind,
                                                            )}
                                                        </span>
                                                        {d.is_default ? (
                                                            <span className="text-muted-foreground ml-1 text-xs">
                                                                (default)
                                                            </span>
                                                        ) : null}
                                                    </span>
                                                    <span className="flex items-center gap-3 text-xs">
                                                        <label className="flex items-center gap-1">
                                                            <Checkbox
                                                                checked={
                                                                    !!d.verified_at
                                                                }
                                                                onCheckedChange={(
                                                                    v,
                                                                ) =>
                                                                    markDomain.mutate(
                                                                        {
                                                                            domain: d.id,
                                                                            verified:
                                                                                v ===
                                                                                true,
                                                                            certificate_ready:
                                                                                !!d.certificate_ready_at,
                                                                        },
                                                                        {
                                                                            onError,
                                                                        },
                                                                    )
                                                                }
                                                            />
                                                            verified
                                                        </label>
                                                        <label className="flex items-center gap-1">
                                                            <Checkbox
                                                                checked={
                                                                    !!d.certificate_ready_at
                                                                }
                                                                onCheckedChange={(
                                                                    v,
                                                                ) =>
                                                                    markDomain.mutate(
                                                                        {
                                                                            domain: d.id,
                                                                            verified:
                                                                                !!d.verified_at,
                                                                            certificate_ready:
                                                                                v ===
                                                                                true,
                                                                        },
                                                                        {
                                                                            onError,
                                                                        },
                                                                    )
                                                                }
                                                            />
                                                            certificate
                                                        </label>
                                                        {d.kind === 'pay' ? (
                                                            <label className="flex items-center gap-1">
                                                                <Checkbox
                                                                    checked={
                                                                        !!d.apple_pay_verified_at
                                                                    }
                                                                    onCheckedChange={(
                                                                        v,
                                                                    ) =>
                                                                        markDomain.mutate(
                                                                            {
                                                                                domain: d.id,
                                                                                verified:
                                                                                    !!d.verified_at,
                                                                                certificate_ready:
                                                                                    !!d.certificate_ready_at,
                                                                                apple_pay_verified:
                                                                                    v ===
                                                                                    true,
                                                                            },
                                                                            {
                                                                                onError,
                                                                            },
                                                                        )
                                                                    }
                                                                />
                                                                Apple Pay
                                                                verified
                                                            </label>
                                                        ) : null}
                                                    </span>
                                                </div>
                                                {d.verification_token &&
                                                !d.verified_at ? (
                                                    <span className="flex items-center gap-2 text-xs">
                                                        <span className="text-fg-2">
                                                            TXT record
                                                        </span>
                                                        <PublicId
                                                            id={
                                                                d.verification_token
                                                            }
                                                            short={false}
                                                        />
                                                    </span>
                                                ) : null}
                                            </li>
                                        ))}
                                        {t.domains.length === 0 ? (
                                            <li className="text-muted-foreground py-2">
                                                Only the platform subdomains.
                                            </li>
                                        ) : null}
                                    </ul>
                                    <form
                                        method="post"
                                        className="flex flex-col gap-2 md:flex-row md:items-end"
                                        data-testid="domain-form"
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            if (!domain.hostname) return;
                                            addDomain.mutate(domain, {
                                                onSuccess: () =>
                                                    setDomain({
                                                        kind: 'pay',
                                                        hostname: '',
                                                    }),
                                                onError,
                                            });
                                        }}
                                    >
                                        <Field id="domain_kind" label="Kind">
                                            <SearchableSelect
                                                id="domain_kind"
                                                label="Kind"
                                                options={optionsOf(
                                                    DOMAIN_KINDS,
                                                )}
                                                value={domain.kind}
                                                clearable={false}
                                                onChange={(v) =>
                                                    v
                                                        ? setDomain({
                                                              ...domain,
                                                              kind: v,
                                                          })
                                                        : undefined
                                                }
                                            />
                                        </Field>
                                        <Field id="hostname" label="Hostname">
                                            <Input
                                                id="hostname"
                                                placeholder="pay.acme.com"
                                                value={domain.hostname}
                                                onChange={(e) =>
                                                    setDomain({
                                                        ...domain,
                                                        hostname:
                                                            e.target.value,
                                                    })
                                                }
                                            />
                                        </Field>
                                        <Button
                                            type="submit"
                                            variant="outline"
                                            disabled={addDomain.isPending}
                                        >
                                            Add domain
                                        </Button>
                                    </form>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-sm font-medium">
                                        Integrations
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <ul
                                        className="divide-y text-sm"
                                        data-testid="tenant-integrations"
                                    >
                                        {integrations.map((i) => (
                                            <li
                                                key={i.provider}
                                                className="flex flex-wrap items-center justify-between gap-2 py-2"
                                            >
                                                <span>
                                                    <span className="font-medium">
                                                        {i.provider}
                                                    </span>
                                                    <span className="text-muted-foreground ml-2 font-mono text-xs">
                                                        {i.secret_name ??
                                                            'no secret'}
                                                    </span>
                                                </span>
                                                <span className="text-xs">
                                                    {i.verified_at ? (
                                                        <>
                                                            verified{' '}
                                                            <DateTime
                                                                value={
                                                                    i.verified_at
                                                                }
                                                                empty="—"
                                                            />
                                                        </>
                                                    ) : (
                                                        'not verified'
                                                    )}
                                                </span>
                                            </li>
                                        ))}
                                        {integrations.length === 0 ? (
                                            <li className="text-muted-foreground py-2">
                                                None configured.
                                            </li>
                                        ) : null}
                                    </ul>
                                    <form
                                        method="post"
                                        className="space-y-3"
                                        data-testid="integration-form"
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            let config: Record<string, unknown>;
                                            try {
                                                config = JSON.parse(
                                                    integration.config || '{}',
                                                );
                                            } catch {
                                                setError(
                                                    'Config must be JSON.',
                                                );
                                                return;
                                            }
                                            setError(null);
                                            setIntegrationCall.mutate(
                                                { ...integration, config },
                                                { onError },
                                            );
                                        }}
                                    >
                                        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                                            <Field
                                                id="provider"
                                                label="Provider"
                                            >
                                                <SearchableSelect
                                                    id="provider"
                                                    label="Provider"
                                                    options={optionsOf(
                                                        PROVIDERS,
                                                    )}
                                                    value={integration.provider}
                                                    clearable={false}
                                                    onChange={(v) =>
                                                        v
                                                            ? setIntegration({
                                                                  ...integration,
                                                                  provider: v,
                                                              })
                                                            : undefined
                                                    }
                                                />
                                            </Field>
                                            <Field
                                                id="secret_name"
                                                label="Secret Manager name"
                                                hint="The secret itself is written by rocnzon's setup script, never through this screen."
                                            >
                                                <Input
                                                    id="secret_name"
                                                    placeholder="bemor-acme-nmi-key"
                                                    value={
                                                        integration.secret_name
                                                    }
                                                    onChange={(e) =>
                                                        setIntegration({
                                                            ...integration,
                                                            secret_name:
                                                                e.target.value,
                                                        })
                                                    }
                                                />
                                            </Field>
                                            <Field
                                                id="config"
                                                label="Config (JSON, public values)"
                                            >
                                                <Input
                                                    id="config"
                                                    className="font-mono text-xs"
                                                    value={integration.config}
                                                    onChange={(e) =>
                                                        setIntegration({
                                                            ...integration,
                                                            config: e.target
                                                                .value,
                                                        })
                                                    }
                                                />
                                            </Field>
                                        </div>
                                        <label className="flex items-center gap-2 text-sm">
                                            <Checkbox
                                                checked={integration.verified}
                                                onCheckedChange={(v) =>
                                                    setIntegration({
                                                        ...integration,
                                                        verified: v === true,
                                                    })
                                                }
                                            />
                                            The verification probe passed
                                        </label>
                                        <Button
                                            type="submit"
                                            variant="outline"
                                            disabled={
                                                setIntegrationCall.isPending
                                            }
                                        >
                                            Save integration
                                        </Button>
                                    </form>
                                </CardContent>
                            </Card>
                            <ConfirmDialog
                                open={confirm === 'activate'}
                                onOpenChange={(o) =>
                                    o ? null : setConfirm(null)
                                }
                                title="Activate this tenant"
                                description="Identity checks the checklist; its hosts start serving."
                                confirmLabel="Activate"
                                busy={activate.isPending}
                                onConfirm={() =>
                                    activate.mutate(undefined, {
                                        onSuccess: () => setConfirm(null),
                                        onError,
                                    })
                                }
                            />
                            {confirm === 'suspend' ? (
                                <ReasonDialog
                                    open
                                    onOpenChange={(o) =>
                                        o ? null : setConfirm(null)
                                    }
                                    title="Suspend this tenant"
                                    description="Every host of the tenant stops serving; merchants and buyers see a notice."
                                    confirmLabel="Suspend"
                                    destructive
                                    busy={suspend.isPending}
                                    error={suspend.error}
                                    onConfirm={(reason) =>
                                        suspend.mutate(reason, {
                                            onSuccess: () => setConfirm(null),
                                        })
                                    }
                                />
                            ) : null}
                        </div>
                    );
                }}
            </DetailBody>
            <div className="mt-4">
                <ActivityCard entity="tenant" id={id} />
            </div>
        </div>
    );
}
